import {ReactElement, useCallback, useState} from "react";
import {EditorGroup, EditorGroupTitle} from "../EditorGroup";
import {FormGroup} from "components/UIComponents/FormGroup";
import {FormattedMessage} from "react-intl";
import {FormInput} from "components/UIComponents/FormInput";
import {EditorItem, isLeafletDocument} from "../../editorUtils";
import {WineItem} from "../../MenuDocumentModel";
import {menuEditorActions, menuEditorSelectors} from "../../editorSlice";
import {assoc, isEmpty} from "ramda";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import { BadgeEditor } from "./BadgeEditor";
import { SliderToggle } from "components/UIComponents/SliderToggle";
import { Slider } from "components/UIComponents/Slider";
import {ShowAppellationControl} from "./editor/ShowAppellationControl";
import {ShowYearControl} from "./editor/ShowYearControl";
import {ImagePicker} from "../image/ImagePicker";
import {authSelectors} from "../../../auth/authSlice";
import {fetchImageCollection, ImageGroup} from "../image/imageCollections";

export interface ExternalWineEditorProps {
    wine: EditorItem<WineItem>
}

export function ExternalWineEditor(props: ExternalWineEditorProps): ReactElement {
    const document = useAppSelector(menuEditorSelectors.document);
    const isLeaflet = document&&isLeafletDocument(document);
    
    const dispatch = useAppDispatch();

    const itemKey = props.wine.itemKey;
    const wineItem = props.wine.menuItem;

    function updateProperty<K extends string, T>(property: K, value: T) {
        dispatch(menuEditorActions.editItem({
            key: itemKey,
            newItem: assoc<T, WineItem, K>(property, value, wineItem) as unknown as WineItem
        }));
    }

    function updateNumberProperty(setState: (str: string) => void, propertyName: string, strValue: string) {
        setState(strValue);
        if (isEmpty(strValue.trim())) {
            updateProperty(propertyName, null);
            return;
        }

        try {
            const numberVal = parseInt(strValue);
            updateProperty(propertyName, numberVal);
        } catch (e) {
            // do nothing if it can't be parse to int
        }
    }

    const setImgUrl = (url: string | null) => {
        let item = assoc('img_url', url, wineItem) as unknown as WineItem;
        if (url) {
            item = assoc('img_scale', 1, item) as unknown as WineItem
        } else {
            item = assoc('img_scale', null, item) as unknown as WineItem
        }

        dispatch(menuEditorActions.editItem({
            key: itemKey,
            newItem: item
        }));
    };

    const userId = useAppSelector(authSelectors.customer);

    const [yearString, setYearString] = useState(wineItem.year?.toString() ?? "");
    const [sizeString, setSizeString] = useState(wineItem.size?.toString() ?? "");

    const fetchWineImageCollection = useCallback((): Promise<ImageGroup[]> =>
            userId ?
                fetchImageCollection({
                    userId,
                    collection: "wine"
                })
                : Promise.resolve([]),
        [userId]
    );


    if (userId === null) return <></>;

    return (
        <div className="flex flex-col items-stretch">
            <EditorGroup>
                <FormGroup groupTitle={<FormattedMessage id="editor.wine.name"/>}>
                    <FormInput
                        value={wineItem.name}
                        onChange={e => updateProperty('name', e.target.value.substring(0, 39))}
                    />
                </FormGroup>

                {isLeaflet&&
                    <FormGroup groupTitle={<FormattedMessage id="editor.wine.title"/>}>
                        <textarea
                            className={`py-3 pl-4 pr-2 w-full
                            text-sm
                            rounded border border-gray-1 outline-0
                            focus:border-focus h-[85px]`}
                            value={wineItem.title}
                            onChange={e => updateProperty('title', e.target.value)}
                        />
                    </FormGroup>
                }

                <div className="grid grid-cols-2 gap-5">
                    <FormGroup groupTitle={<FormattedMessage id="editor.wine.year"/>}>
                        <FormInput
                            value={yearString}
                            type="number"
                            onChange={e => updateNumberProperty(setYearString, 'year', e.target.value)}
                        />
                    </FormGroup>
                    <FormGroup groupTitle={<FormattedMessage id="editor.wine.size"/>}>
                        <FormInput
                            value={sizeString}
                            type="number"
                            onChange={e => updateNumberProperty(setSizeString, 'size', e.target.value)}
                        />
                    </FormGroup>
                </div>

                {!isLeaflet&&<ShowYearControl wine={props.wine}/>}

                <FormGroup groupTitle={<FormattedMessage id="editor.wine.country"/>}>
                    <FormInput
                        value={wineItem.country ?? ""}
                        onChange={e => updateProperty('country', e.target.value)}
                    />
                </FormGroup>

                <FormGroup groupTitle={<FormattedMessage id="editor.wine.region"/>}>
                    <FormInput
                        value={wineItem.region ?? ""}
                        onChange={e => updateProperty('region', e.target.value)}
                    />
                </FormGroup>

                <FormGroup groupTitle={<FormattedMessage id="editor.wine.producer"/>}>
                    <FormInput
                        value={wineItem.producer ?? ""}
                        onChange={e => updateProperty('producer', e.target.value)}
                    />
                </FormGroup>

                <FormGroup groupTitle={<FormattedMessage id="editor.wine.grapes"/>}>
                    <FormInput
                        value={wineItem.grapes ?? ""}
                        onChange={e => updateProperty('grapes', e.target.value)}
                    />
                </FormGroup>

                <FormGroup groupTitle={<FormattedMessage id="editor.image"/>}>
                    <ImagePicker
                        images={fetchWineImageCollection}
                        currentImage={wineItem.img_url??null}
                        onPick={setImgUrl}
                        allowClear={true}
                        upload={{
                            userId: userId,
                            collection: 'wine',
                            allowResize: true,
                            resizeAspect: 40/114
                        }}
                    />
                </FormGroup>

            </EditorGroup>

            <EditorGroup>
                <EditorGroupTitle>
                    <FormattedMessage id="editor.wine.description"/>
                </EditorGroupTitle>

                {!isLeaflet&&<div className="flex items-center gap-2">
                    <div className="flex-auto text-sm">
                        <FormattedMessage id="editor.wine.details"/>
                    </div>
                    <div className="flex-none h-7 w-12">
                        <SliderToggle
                            enabled={wineItem.detailed}
                            setEnabled={enabled => updateProperty('detailed', enabled)}
                        />
                    </div>
                </div>}

                {(wineItem.detailed||isLeaflet) && <>
                    {!isLeaflet&&<div className="flex items-center gap-2">
                        <span className="flex-auto text-sm">
                            <FormattedMessage id="editor.wine.showDescription"/>
                        </span>
                        <div className="flex-none h-7 w-12">
                            <SliderToggle
                                enabled={wineItem.showDescription}
                                setEnabled={showDescription => updateProperty('showDescription', showDescription)}
                            />
                        </div>
                    </div>}

                    {(wineItem.showDescription||isLeaflet) && <FormGroup groupTitle="">
                        <textarea
                            className="py-3 pl-4 pr-2 w-full
                            text-sm
                            rounded border border-gray-1 outline-0
                            focus:border-focus h-[85px]"
                            value={wineItem.text}
                            onChange={e => updateProperty('text', e.target.value)}
                        />
                    </FormGroup>}

                    {!isLeaflet&&<div className="flex items-center gap-2">
                        <span className="flex-auto text-sm">
                            <FormattedMessage id="editor.wine.showTasteSweetness"/>
                        </span>
                        <div className="flex-none h-7 w-12">
                            <SliderToggle
                                enabled={wineItem.showTasteSweetness !== undefined && wineItem.showTasteSweetness !== null ? wineItem.showTasteSweetness : true}
                                setEnabled={showTasteSweetness => updateProperty('showTasteSweetness', showTasteSweetness)}
                            />
                        </div>
                    </div>}


                    {(isLeaflet || wineItem.showTasteSweetness === undefined || wineItem.showTasteSweetness === null || wineItem.showTasteSweetness === true) && <>
                        <FormGroup groupTitle={<FormattedMessage id="editor.wine.taste"/>}>
                            <Slider
                                min={1}
                                max={10}
                                value={wineItem.taste || 1}
                                onChange={taste => updateProperty('taste', taste)}
                            />
                        </FormGroup>


                        <FormGroup groupTitle={<FormattedMessage id="editor.wine.sweetness"/>}>
                            <Slider
                                min={1}
                                max={10}
                                value={wineItem.sweetness || 1}
                                onChange={sweetness => updateProperty('sweetness', sweetness)}
                            />
                        </FormGroup>
                    </>}

                    {!isLeaflet&&<div className="flex items-center gap-2">
                        <span className="flex-auto text-sm">
                            <FormattedMessage id="editor.wine.showSuitableFood"/>
                        </span>
                        <div className="flex-none h-7 w-12">
                            <SliderToggle
                                enabled={wineItem.showSuitableFood !== undefined && wineItem.showSuitableFood !== null ? wineItem.showSuitableFood : true}
                                setEnabled={showSuitableFood => updateProperty('showSuitableFood', showSuitableFood)}
                            />
                        </div>
                    </div>}

                    {(isLeaflet || wineItem.showSuitableFood === undefined || wineItem.showSuitableFood === null || wineItem.showSuitableFood) && <FormGroup groupTitle={<></>}>
                        <FormInput
                            value={wineItem.suitableFood}
                            onChange={e => updateProperty('suitableFood', e.target.value)}
                        />
                    </FormGroup>}

                    <ShowAppellationControl wine={props.wine}/>
                </>}
            </EditorGroup>

            <BadgeEditor wine={props.wine}/>
        </div>
    );
}
